import classNames from 'clsx';
import { get, isEqual } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import CasinoAllButton from '@swf/components/CasinoAllButton/CasinoAllButton';
import CasinoFilter from '@swf/components/CasinoFilter/CasinoFilter';
import CasinoSliderButtons from '@swf/components/CasinoSliderButtons/CasinoSliderButtons';
import type { SliderHandles, State as SliderState } from '@swf/components/Slider/Slider';
import StarsNav, { Variant as NavVariant } from '@swf/components/StarsNavigation/StarsNavigation';
import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';
import type { Page } from '@swf/core/types/CasinoGame';
import type { Filter, FilterType } from '@swf/core/utils/casinoFilters';
import { menus } from '@swf/core/utils/casinoFilters';
import { CasinoIcon } from '@swf/generated/types/common/Casino';

import styles from './CasinoGameHeader.scss';
import CasinoHeaderTitle from './components/CasinoHeaderTitle/CasinoHeaderTitle';

interface Props {
  active?: number;
  data?: Page[];
  filters: FilterType | string;
  filterURL?: string;
  hideBuiltInFilters?: boolean;
  icon: CasinoIcon;
  moreLink: string;
  title: string;
  slide: boolean;
  stickyHeader: boolean;
  tab?: string;
  widgetRef: React.RefObject<HTMLDivElement>;
  onFilter?(active: number): void;
  sliderRef?: React.RefObject<SliderHandles>;
  sliderState?: SliderState;
  isValid?(filter: Filter): boolean;
}

type FiltersProps = Props & {
  onClick(): void;
  isValid?(filter: Filter): boolean;
};

export const validByCheck = (filter: Filter) => true; // TODO: validByCheck removal and all uses after CasinoGameWidget.tsx is removed

const CasinoGameHeader: React.FC<Props> = (props) => {
  const isMobile = useSelector(select.device.isMobile);
  const isEmbedded = useSelector(select.site.isEmbedded);

  const {
    moreLink,
    title,
    slide,
    icon,
    filters,
    hideBuiltInFilters = false,
    widgetRef,
    stickyHeader,
    sliderRef,
    sliderState
  } = props;

  const headerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<HTMLDivElement>(null);

  const showScroll = !isMobile && slide && ![CasinoIcon.icnHeroWide].includes(icon);

  const scrollToWidget = useCallback(() => {
    const widget = widgetRef.current;
    const header = headerRef.current;

    if (widget && header && stickyHeader) {
      const { top, height } = header.getBoundingClientRect();
      const headerOffset = top + height;

      if (window.pageYOffset > headerOffset) {
        const gameListOffest = (widget.lastElementChild as HTMLElement).offsetTop;
        const documentScrollingElement = document.scrollingElement;

        if (documentScrollingElement) {
          documentScrollingElement.scrollTop = gameListOffest - headerOffset;
        }
      }
    }
  }, [stickyHeader, widgetRef]);

  const [sticky, setSticky] = useState(isEmbedded ? stickyHeader : false);

  const handleIntersection = useCallback((records) => setSticky(!records[0].isIntersecting), []);

  useEffect(() => {
    if (!isEmbedded && stickyHeader && observerRef.current) {
      const observer = new IntersectionObserver(handleIntersection, { rootMargin: '-44px' }); // 44 is the height of the nav bar
      observer.observe(observerRef.current);

      return () => observer.disconnect();
    }
    return () => undefined;
  }, [isEmbedded, stickyHeader, observerRef, handleIntersection]);

  return (
    <>
      <div ref={observerRef} />
      {!hideBuiltInFilters && (
        <header
          className={classNames(styles.header, {
            [isEmbedded ? styles.stickyEmbedded : styles.sticky]: stickyHeader,
            [styles.stickyBackground]: sticky
          })}
          ref={headerRef}
        >
          <CasinoHeaderTitle moreLink={moreLink} title={title} />
          {!!menus[filters] && <Filters {...props} onClick={scrollToWidget} />}
          {(moreLink || showScroll) && (
            <div className={styles.buttons}>
              {moreLink && <CasinoAllButton href={moreLink} />}
              {showScroll && sliderRef && sliderState && (
                <CasinoSliderButtons sliderRef={sliderRef} sliderState={sliderState} />
              )}
            </div>
          )}
        </header>
      )}
    </>
  );
};

const Filters: React.FC<FiltersProps> = ({
  data,
  filters,
  onFilter,
  onClick,
  active,
  isValid,
  filterURL
}) => {
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const payload = menus[filters].reduce((acc, { checkQuery }) => {
    checkQuery && (acc[checkQuery] = false);
    return acc;
  }, new Map<string, boolean>());

  const [checkedFilters, setCheckedFilters] = useState<Map<string, boolean>>(payload);
  const request = useRequest();

  useEffect(() => {
    const toCheck = Object.keys(payload);
    toCheck.length > 0 &&
      request({ service: 'casino.games.filters', body: { filters: toCheck } })
        .toPromise()
        .then((result) => result?.body?.filters || {})
        .then((enabledFilters) => {
          setCheckedFilters(enabledFilters as Map<string, boolean>);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, filters]);

  return (
    <StarsNav className={styles.filters} variant={NavVariant.Transparent}>
      {menus[filters].map((filter, index) => (
        <CasinoFilter
          active={index === active}
          hidden={
            (filter.auth && !isAuthenticated) ||
            (isValid
              ? isValid === validByCheck && filter.checkQuery
                ? !checkedFilters[filter.checkQuery]
                : !isValid(filter)
              : get(data, [index, 'games'], []).length < (filter.slug !== 'popular' ? 3 : 1))
          }
          key={filter.name.id}
          onClick={() => {
            if (index !== active) {
              onFilter && onFilter(index);
              onClick();
            }
          }}
          href={filterURL ? filterURL.replace(':slug', filter.slug) : undefined}
          translation={filter.name}
        />
      ))}
    </StarsNav>
  );
};

export default React.memo(CasinoGameHeader, isEqual);
